import $ from "jquery";

/**
 * Controls a form that posts to AJAX for HubSpot.
 */
export default class AJAXForm {
    /**
     * @var {jquery|null}
     */
    form = null;

    /**
     * WordPress AJAX action to post this form.
     * 
     * @var {String|null}
     */
    action = null;

    /**
     * Name of the page, to be passed into the HubSpot so it can be linked up to the source appropriately.
     * 
     * @var {String|null}
     */
    pageName = null;

    /**
     * List of input elements
     * 
     * @var {JQuery<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>|JQuery}
     */
    inputs = $();

    /**
     * The method to call on successful submit.
     * 
     * @var {Function|null} successCb
     */
    successCb = null;

    /**
     * The method to call on validation failure.
     * 
     * @var {Function|null} invalidCb
     */
    invalidCb = null;

    /**
     * The method to call on failure to submit.
     * 
     * @var {Function|null} failureCb
     */
    failureCb = null;

    /**
     * Class constructor.
     * 
     * @param {String} formElementId 
     */
    constructor( formElementId ) {
        this.form       = $( `#${ formElementId }` );
        this.pageName   = document.querySelector( "title" )?.innerText ?? null;

        // Find all of the form inputs and bind relevant handlers. Also extracts useful information from the hidden items.
        this.resolveInputs().each( ( function ( idx, element ) { 
            // Exclude hidden elements from the inputs list.
            if ( element.type === "hidden" ) {
                // Grab the AJAX action name.
                if ( element.name === "action" ) {
                    this.action = element.value;
                }

                // Update page name if we can / have to.
                if ( ( ! this.pageName ) && element.name === "page_name" ) {
                    this.pageName = element.value;
                }

                return;
            }

            // Attach required event handlers.
            $( element ).on( {
                "input":    this.onInput.bind( this ),
                "focusout": this.onFocusout.bind( this )
            } )
        } ).bind( this ) );

        // Disable builtin HTML form validation and bind the submit event.
        this.form.prop( "noValidate", true ).on( {
            "submit": this.onSubmitForm.bind( this )
        } );
    }

    /**
     * Set the method to be called on successful handle.
     * 
     * @param {Function} method 
     * @returns {AJAXForm}
     */
    handleSuccess( method ) {
        this.successCb = method;

        return this;
    }

    /**
     * Set the method to be called when the form inputs could not be validated.
     * 
     * @param {Function|null} method 
     * @returns 
     */
    handleInvalid( method ) {
        this.invalidCb = method;

        return this;
    }

    /**
     * Set the method to be called on failure to handle.
     * 
     * @param {Function} method 
     * @returns {AJAXForm}
     */
    handleError( method ) {
        this.failureCb = method;

        return this;
    }

    /**
     * Called on `input` event to one of the form elements.
     * 
     * @param {InputEvent} event 
     */
    onInput( event ) { }

    /**
     * Called on `focusout` event to one of the form elements.
     * 
     * @param {FocusEvent} event 
     */
    onFocusout( event ) {
        AJAXForm.setValidationState( event.target, this.validateInput( event.target ) );
    }

    /**
     * Called on form submit.
     * 
     * @param {SubmitEvent} event 
     */
    onSubmitForm( event ) {
        // Disable normal submit attempt
        event.preventDefault();
        
        // Iterate on the inputs and compile the list of validation results.
        var validationResults = [];

        this.resolveInputs().each( ( function ( idx, element ) { 
            // Exclude hidden elements & non-required elements from the inputs list.
            if ( element.type === "hidden" || ( ! element.required ) ) {
                return;
            }
            
            const state = this.validateInput( element );

            AJAXForm.setValidationState( element, state, true );
            validationResults.push( state );
        } ).bind( this ) );

        // If any of the validation results are null or false then validation has not passed.
        if ( validationResults.includes( false ) || validationResults.includes( null ) ) {
            if ( this.invalidCb ) this.invalidCb.bind( this )();
            return;
        }

        this.form.find( `[type="submit"]` ).prop( "disabled", true );

        // AJAX post the form.
        // this.postForm().then( this.successCb.bind( this ) ?? ( () => {} ) ).catch( this.failureCb.bind( this ) ?? ( () => {} ) );
    }

    /**
     * Validates a given input. 
     * 
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input 
     * @return {Boolean|null} True = successful validation, False = completely invalid, Null = no validation state (but technically not valid also).
     */
    validateInput( input ) {
        if ( input.value.trim().length === 0 || ( ! input.required ) ) {
            return null;
        }

        if ( input instanceof HTMLTextAreaElement ) {
            if ( input.value.length === 0 ) {
                return null;
            }

            return input.value.length > 0;
        }

        switch ( input.type ) {
        case "email":
            return !! AJAXForm.validateEmail( input.value );
        default:
            return input.value.trim().length > 0;
        }
    }

    /**
     * Finds / gets all inputs within the form.
     * 
     * @returns {JQuery<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>}
     */
    resolveInputs() {
        // Return cached inputs.
        if ( this.inputs.length ) {
            return this.inputs;
        }

        // Locate all inputs, and return them.
        this.inputs = this.form.find( "input, select, textarea" );
        return this.inputs;
    }

    /**
     * Collects the form field output.
     * 
     * @returns {{}}
     */
    collectFields() {
        var fields = {};

        this.resolveInputs().each( ( function ( idx, element ) { 
            // Exclude hidden elements & non-required elements from the inputs list.
            if ( element.type === "hidden" ) {
                return;
            }

            fields[ element.name ] = element.value;
        } ).bind( this ) );

        return fields;
    }

    /**
     * Sends off the form and waits for results.
     * 
     * @return {Promise<{}>}
     */
    async postForm() {
        const fields = this.collectFields();

        return $.ajax( {
            url:        `${window.location.origin}/wp-admin/admin-ajax.php`,
            method:     "POST",
            dataType:   "json",
            data: { 
                action: this.action,
                ...fields // Spread fields into AJAX data.
            }
        } )
    }

    /**
     * Sets the validation classes of an input based on it's validated state.
     * 
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input 
     * @param {Boolean|null} state 
     */
    static setValidationState( input, state = null, nullMeansFalse = false ) {
        if ( state === true ) {
            $( input ).addClass( "valid" ).removeClass( "invalid" );
            $( input ).parents( ".validator" ).addClass( "valid" ).removeClass( "invalid" );
        } else if ( state === false || ( ( state === null ) && nullMeansFalse) ) {
            $( input ).addClass( "invalid" ).removeClass( "valid" );
            $( input ).parents( ".validator" ).addClass( "invalid" ).removeClass( "valid" );
        } else {
            $( input).removeClass( "valid" ).removeClass( "invalid" );
            $( input).parents( ".validator" ).removeClass( "valid" ).removeClass( "invalid" );
        }
    }

    /**
     * Validate the email address.
     * 
     * @param {String} email 
     * @returns {Boolean}
     */
    static validateEmail( email ) {
        return String( email )
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    }
}